/**
 * The fixed-rate authoritative game loop.
 *
 * Per `docs/02_TECHNICAL_ARCHITECTURE.md` the simulation runs at a fixed 10
 * ticks/sec — coarser than a twitch shooter because border-pressure resolution,
 * resource accrual and world events don't need 60Hz. Named-unit movement
 * interpolates client-side between ticks for smoothness.
 *
 * ## Why the intent queue is the most important thing in this file
 *
 * Intents arriving between ticks are buffered and applied at the start of the
 * next tick, in arrival order. Nothing else in the codebase is allowed to mutate
 * game state.
 *
 * This is the seam that makes multiplayer cheap. Today a bot pushes into this
 * queue in-process. Later a WebSocket handler pushes into the exact same queue.
 * The simulation cannot tell the difference and does not change.
 *
 * Multiplayer is now scheduled late in the roadmap, which makes this discipline
 * *more* important, not less: the seam won't be exercised by a second
 * implementation for a long time, so it has to be respected on trust. If you
 * ever find yourself wanting to mutate state from outside a tick, that is the
 * bug — not the queue.
 */
/** Simulation rate, per docs/02. Revisit empirically once the loop is loaded. */
export const TICK_RATE_HZ = 10;
export const TICK_MS = 1000 / TICK_RATE_HZ;
/**
 * Most ticks the loop will run in one catch-up burst. Without this, a process
 * that stalls for 30 seconds tries to replay 300 ticks in one go and stalls
 * further — the classic death spiral.
 */
export const MAX_CATCHUP_TICKS = 5;
/** Warn above this fraction of the tick budget. */
const SLOW_TICK_THRESHOLD = 0.5;
/**
 * Per-kingdom cap on queued intents. A buggy bot or (later) a hostile client
 * cannot grow the queue without bound. Real rate limiting arrives with the
 * multiplayer phase; this is just the safety net.
 */
export const MAX_QUEUED_INTENTS_PER_KINGDOM = 64;
export class GameLoop {
    onTick;
    onSlowTick;
    now;
    tickMs;
    maxCatchupTicks;
    budgetMs;
    queue = [];
    queuedPerKingdom = new Map();
    tickCount = 0;
    accumulatorMs = 0;
    lastClockMs = 0;
    timer = null;
    lastTickMs = 0;
    maxTickMs = 0;
    droppedTicks = 0;
    rejectedIntents = 0;
    constructor(options) {
        const tickRateHz = options.tickRateHz ?? TICK_RATE_HZ;
        if (!(tickRateHz > 0)) {
            throw new Error(`tickRateHz must be positive, got ${tickRateHz}`);
        }
        this.onTick = options.onTick;
        this.onSlowTick = options.onSlowTick;
        this.now = options.now ?? (() => performance.now());
        this.tickMs = 1000 / tickRateHz;
        this.maxCatchupTicks = options.maxCatchupTicks ?? MAX_CATCHUP_TICKS;
        this.budgetMs = this.tickMs * SLOW_TICK_THRESHOLD;
    }
    get tick() {
        return this.tickCount;
    }
    get running() {
        return this.timer !== null;
    }
    getStats() {
        return {
            tick: this.tickCount,
            running: this.running,
            lastTickMs: this.lastTickMs,
            maxTickMs: this.maxTickMs,
            queueDepth: this.queue.length,
            droppedTicks: this.droppedTicks,
            rejectedIntents: this.rejectedIntents,
        };
    }
    /**
     * Buffer an intent for the next tick. Returns false if the kingdom's queue is
     * full — callers may log it, but must not retry in a tight loop.
     *
     * This method deliberately does no validation beyond the queue cap. Whether an
     * intent is *legal* is a question about authoritative game state, which only
     * exists inside a tick, so it is answered by the intent handler in
     * `server/src/game`, never here.
     */
    submitIntent(kingdomId, intent) {
        const queued = this.queuedPerKingdom.get(kingdomId) ?? 0;
        if (queued >= MAX_QUEUED_INTENTS_PER_KINGDOM) {
            this.rejectedIntents += 1;
            return false;
        }
        this.queue.push({ kingdomId, intent, receivedTick: this.tickCount });
        this.queuedPerKingdom.set(kingdomId, queued + 1);
        return true;
    }
    start() {
        if (this.timer !== null)
            return;
        this.lastClockMs = this.now();
        const pump = () => {
            const clockMs = this.now();
            const elapsedMs = clockMs - this.lastClockMs;
            this.lastClockMs = clockMs;
            this.advance(elapsedMs);
            // Sleep until the next tick boundary rather than a fixed interval, so
            // slow ticks don't compound into drift.
            const delayMs = Math.max(0, this.tickMs - this.accumulatorMs);
            this.timer = setTimeout(pump, delayMs);
        };
        this.timer = setTimeout(pump, this.tickMs);
    }
    stop() {
        if (this.timer === null)
            return;
        clearTimeout(this.timer);
        this.timer = null;
    }
    /**
     * Feed elapsed wall-clock time to the loop and run whatever whole ticks it
     * buys. Returns the number of ticks executed.
     *
     * Exposed publicly because it makes the loop testable without real timers,
     * and because headless simulation (balance runs, deterministic replay tests)
     * wants to advance the game as fast as the CPU allows rather than in real
     * time.
     */
    advance(elapsedMs) {
        if (elapsedMs < 0)
            return 0;
        this.accumulatorMs += elapsedMs;
        let executed = 0;
        while (this.accumulatorMs >= this.tickMs && executed < this.maxCatchupTicks) {
            this.accumulatorMs -= this.tickMs;
            this.step();
            executed += 1;
        }
        // Still behind after a full catch-up burst: give up on the missed time
        // rather than trying to replay it forever.
        if (this.accumulatorMs >= this.tickMs) {
            this.droppedTicks += Math.floor(this.accumulatorMs / this.tickMs);
            this.accumulatorMs = 0;
        }
        return executed;
    }
    /** Run exactly one tick, ignoring the clock. For tests and replay tooling. */
    stepOnce() {
        this.step();
    }
    step() {
        const intents = this.queue;
        this.queue = [];
        this.queuedPerKingdom.clear();
        this.tickCount += 1;
        const startedAt = this.now();
        this.onTick({ tick: this.tickCount, dtMs: this.tickMs, intents });
        const durationMs = this.now() - startedAt;
        this.lastTickMs = durationMs;
        if (durationMs > this.maxTickMs)
            this.maxTickMs = durationMs;
        if (durationMs > this.budgetMs && this.onSlowTick) {
            this.onSlowTick({ tick: this.tickCount, durationMs, budgetMs: this.budgetMs });
        }
    }
}
//# sourceMappingURL=tick.js.map